/**
 * /app/screeners — Скринеры v2 (PULSE-268).
 *
 * Один раздел на обе роли:
 *   user  — видит public/назначенные скринеры, подключает инстансы со
 *           своими настройками (метрики, chat_id, cooldown), тестирует чат,
 *           смотрит ленту сигналов с forward-return'ами.
 *   admin — плюс: видимость (off/public/assigned), назначение юзерам,
 *           создание/удаление signal-скринеров.
 *
 * Данные: useScreenersApi (бэкенд role-aware). Доставка — host-сервисы
 * universal-screener / screener-router.
 */
import { useMemo, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Plus, Pencil, Trash2, Send, X, Users as UsersIcon, Radio, Activity,
  FlaskConical,
  Trophy,
} from 'lucide-react'

import {
  Field, INPUT, MetricBlocks, autoName, filtersSummary, metricFormsFromParams, metricsToParams,
  type MetricForms,
} from '@/components/screeners/metricForm'
import { EventViewer } from '@/components/screeners/EventViewer'
import {
  type AvailableScreener,
  type BacktestEvent,
  type Instance,
  type InstanceUpsert,
  type UniversalParams,
  type ScreenerDef,
  type SignalRow,
  type Visibility,
  useCreateDef, useCreateInstance, useDeleteDef, useDeleteInstance,
  useScreenerSignals, useScreenersOverview, useSetDefAccess,
  useSignalTypes, useTestInstance, useUpdateDef, useUpdateInstance,
} from '@/hooks/useScreenersApi'
import { useAdminUsers } from '@/hooks/useAdminUsersApi'
import { cn } from '@/lib/cn'

const VIS_LABEL: Record<Visibility, string> = {
  off: 'выключен',
  public: 'публичный',
  assigned: 'по назначению',
}

function Modal({ title, onClose, children, wide }: {
  title: string; onClose: () => void; children: ReactNode; wide?: boolean
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className={cn('bg-ink border border-line w-full mt-10 p-5', wide ? 'max-w-2xl' : 'max-w-lg')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="text-textFaint hover:text-textDim">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

// ─── Форма инстанса ──────────────────────────────────────────────────

interface InstForm {
  label: string
  enabled: boolean
  chat_id: string
  cooldown_min: string
  // universal
  metrics: MetricForms
  // signal
  direction: string
  symbol: string
  min_score: string
  template: string
}

function emptyForm(def: ScreenerDef): InstForm {
  const m = metricFormsFromParams(def.default_params)
  return {
    label: '', enabled: true, chat_id: '', cooldown_min: '15',
    metrics: m,
    direction: '', symbol: '', min_score: '', template: '',
  }
}

function instToForm(def: ScreenerDef, i: Instance): InstForm {
  const f = emptyForm(def)
  f.label = i.label
  f.enabled = i.enabled
  f.chat_id = i.chat_id
  f.cooldown_min = String(i.cooldown_min)
  if (def.kind === 'universal') {
    const m = metricFormsFromParams(i.params)
    f.metrics = m
  } else {
    f.direction = typeof i.params.direction === 'string' ? i.params.direction : ''
    f.symbol = typeof i.params.symbol === 'string' ? i.params.symbol : ''
    f.min_score = i.params.min_score != null ? String(i.params.min_score) : ''
    f.template = typeof i.params.template === 'string' ? i.params.template : ''
  }
  return f
}

function formToBody(def: ScreenerDef, f: InstForm): InstanceUpsert | string {
  const chat = f.chat_id.trim()
  if (f.enabled && !chat) return 'Укажите chat_id — куда боту слать сигналы'
  const cooldown = Number(f.cooldown_min)
  if (Number.isNaN(cooldown) || cooldown < 0 || cooldown > 1440) {
    return 'Cooldown: число минут от 0 до 1440'
  }
  let params: Record<string, unknown> = {}
  if (def.kind === 'universal') {
    const p = metricsToParams(f.metrics)
    if (typeof p === 'string') return p
    params = p
  } else {
    if (f.direction) params.direction = f.direction
    if (f.symbol.trim()) params.symbol = f.symbol.trim().toUpperCase()
    if (f.min_score.trim() && !Number.isNaN(Number(f.min_score))) {
      params.min_score = Number(f.min_score)
    }
    if (f.template.trim()) params.template = f.template
  }
  return {
    label: f.label.trim(),
    enabled: f.enabled,
    params,
    chat_id: chat,
    cooldown_min: cooldown,
  }
}

function InstanceModal({ def, inst, initParams, initName, defaultChat, onClose }: {
  def: ScreenerDef; inst: Instance | null; initParams?: UniversalParams; initName?: string
  /** чат из уже подключённых скринеров — чтобы не искать chat_id заново */
  defaultChat?: string
  onClose: () => void
}) {
  const create = useCreateInstance()
  const update = useUpdateInstance()
  const [form, setForm] = useState<InstForm>(() => {
    if (inst) return instToForm(def, inst)
    const f = { ...emptyForm(def), chat_id: defaultChat ?? '' }
    // PULSE-372: «Подключить» со страницы подбора сетапов — условия готовы
    return initParams ? { ...f, metrics: metricFormsFromParams(initParams), label: initName ?? '' } : f
  })
  const formParams = def.kind === 'universal'
    ? metricsToParams(form.metrics)
    : null
  const suggested = formParams && typeof formParams !== 'string' ? autoName(formParams) : ''
  const [err, setErr] = useState<string | null>(null)
  const busy = create.isPending || update.isPending

  async function submit() {
    setErr(null)
    const body = formToBody(def, form)
    if (typeof body === 'string') { setErr(body); return }
    try {
      if (inst) await update.mutateAsync({ id: inst.id, body })
      else await create.mutateAsync({ screenerId: def.id, body })
      onClose()
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setErr(detail ?? 'Не удалось сохранить. Проверьте поля.')
    }
  }

  return (
    <Modal
      title={inst ? `${def.name} — настройки` : `Подключить: ${def.name}`}
      onClose={onClose}
      wide={def.kind === 'universal'}
    >
      <div className="space-y-3">
        {def.kind === 'universal' && (
          <>
            <p className="text-xs text-textDim leading-snug">
              Отметьте нужные условия — сигнал придёт, когда выполнены <b>все отмеченные сразу</b>.
              Можно начать с готового сетапа и поправить цифры. Подобрать условия на истории —
              на странице <Link to="/app/screeners/setups" className="text-fire hover:underline">«Подбор сетапов»</Link>.
            </p>
            <MetricBlocks
              value={form.metrics}
              onChange={(m) => setForm({ ...form, metrics: m })}
            />
          </>
        )}
        {def.kind === 'signal' && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Направление">
              <select
                value={form.direction}
                onChange={(e) => setForm({ ...form, direction: e.target.value })}
                className={INPUT}
              >
                <option value="">любое</option>
                <option value="long">long</option>
                <option value="short">short</option>
              </select>
            </Field>
            <Field label="Символ">
              <input
                value={form.symbol}
                onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                className={INPUT} placeholder="любой"
              />
            </Field>
            <Field label="Мин. score">
              <input
                value={form.min_score}
                onChange={(e) => setForm({ ...form, min_score: e.target.value })}
                className={INPUT} placeholder="—" inputMode="numeric"
              />
            </Field>
          </div>
        )}

        <Field
          label="Название"
          hint={def.kind === 'universal'
            ? 'Стоит в заголовке каждого сигнала в Telegram — по нему видно, какой из ваших скринеров сработал. Пусто — название по условиям.'
            : 'Необязательно.'}
        >
          <input
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            className={INPUT} placeholder={suggested || 'Моя группа'}
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            label="Telegram-чат (chat_id)"
            hint="Telegram-чат (группа или личка). Добавьте бота в чат."
          >
            <input
              value={form.chat_id}
              onChange={(e) => setForm({ ...form, chat_id: e.target.value })}
              className={INPUT} placeholder="-100123456789"
            />
          </Field>
          <Field label="Пауза, мин" hint="Не чаще одного сигнала по одной монете за это время.">
            <input
              value={form.cooldown_min}
              onChange={(e) => setForm({ ...form, cooldown_min: e.target.value })}
              className={INPUT} inputMode="numeric"
            />
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm text-textDim">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          />
          Включён
        </label>

        {err && <div className="text-rose text-sm">{err}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm text-textDim border border-line hover:bg-line/20"
          >
            Отмена
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-pulse/15 text-pulse border border-pulse/30 hover:bg-pulse/25 disabled:opacity-50"
          >
            <Send size={14} /> Сохранить
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ─── Админ: назначение юзеров ────────────────────────────────────────

function AccessModal({ def, onClose }: { def: ScreenerDef; onClose: () => void }) {
  const { data } = useAdminUsers({ status: 'active', page: 1 })
  const setAccess = useSetDefAccess()
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(def.assigned_user_ids ?? []))

  const users = (data?.items ?? []).filter((u) => u.email)

  async function save() {
    try {
      await setAccess.mutateAsync({ id: def.id, userIds: Array.from(selected) })
      onClose()
    } catch { /* список обновится с сервера */ }
  }

  return (
    <Modal title={`Назначение: ${def.name}`} onClose={onClose}>
      <p className="text-xs text-textDim mb-3">
        Кому доступен скринер при видимости «по назначению».
      </p>
      <div className="max-h-72 overflow-y-auto border border-line divide-y divide-line/50">
        {users.map((u) => (
          <label key={u.id} className="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-line/20">
            <input
              type="checkbox"
              checked={selected.has(u.id)}
              onChange={(e) => {
                const next = new Set(selected)
                if (e.target.checked) next.add(u.id)
                else next.delete(u.id)
                setSelected(next)
              }}
            />
            <span>{u.email}</span>
            {u.role === 'admin' && (
              <span className="text-2xs text-fire border border-fire/30 px-1">admin</span>
            )}
          </label>
        ))}
        {users.length === 0 && (
          <div className="px-3 py-4 text-sm text-textFaint">Нет активных юзеров.</div>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <button
          onClick={onClose}
          className="px-3 py-2 text-sm text-textDim border border-line hover:bg-line/20"
        >
          Отмена
        </button>
        <button
          onClick={save}
          disabled={setAccess.isPending}
          className="px-3 py-2 text-sm bg-pulse/15 text-pulse border border-pulse/30 hover:bg-pulse/25 disabled:opacity-50"
        >
          Сохранить
        </button>
      </div>
    </Modal>
  )
}

// ─── Админ: создание signal-скринера ─────────────────────────────────

function CreateDefModal({ onClose }: { onClose: () => void }) {
  const createDef = useCreateDef()
  const { data: stData } = useSignalTypes(true)
  const [form, setForm] = useState({ key: '', name: '', description: '', signal_type: '' })
  const [err, setErr] = useState<string | null>(null)

  async function submit() {
    setErr(null)
    if (!form.name.trim() || !form.signal_type) {
      setErr('Название и тип сигнала обязательны'); return
    }
    const key = form.key.trim() || form.signal_type
    try {
      await createDef.mutateAsync({
        key, kind: 'signal', signal_type: form.signal_type,
        name: form.name.trim(), description: form.description, visibility: 'off',
      })
      onClose()
    } catch {
      setErr('Не удалось создать (key уже занят?)')
    }
  }

  return (
    <Modal title="Новый signal-скринер" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Тип сигнала (divergence_alerts)">
          <select
            value={form.signal_type}
            onChange={(e) => setForm({ ...form, signal_type: e.target.value })}
            className={INPUT}
          >
            <option value="">— выбрать —</option>
            {(stData?.signal_types ?? []).map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>
        <Field label="Название">
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={INPUT} placeholder="PUMP/DUMP"
          />
        </Field>
        <Field label="Описание">
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3} className={cn(INPUT, 'resize-y')}
          />
        </Field>
        {err && <div className="text-rose text-sm">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-textDim border border-line hover:bg-line/20">
            Отмена
          </button>
          <button
            onClick={submit}
            disabled={createDef.isPending}
            className="px-3 py-2 text-sm bg-pulse/15 text-pulse border border-pulse/30 hover:bg-pulse/25 disabled:opacity-50"
          >
            Создать
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ─── Карточка скринера ───────────────────────────────────────────────

/** Фильтры signal-скринера (universal называется autoName). */
function paramsSummary(i: Instance): string {
  const parts: string[] = []
  if (i.params.direction) parts.push(String(i.params.direction))
  if (i.params.symbol) parts.push(String(i.params.symbol))
  if (i.params.min_score != null) parts.push(`score≥${i.params.min_score}`)
  return parts.join(' · ') || 'все сигналы'
}

function ScreenerCard({ item, isAdmin, connectParams, connectName }: {
  item: AvailableScreener; isAdmin: boolean; connectParams?: UniversalParams; connectName?: string
}) {
  const { def, instances } = item
  const update = useUpdateInstance()
  const del = useDeleteInstance()
  const test = useTestInstance()
  const updateDef = useUpdateDef()
  const deleteDef = useDeleteDef()

  const [modal, setModal] = useState<'new' | Instance | 'access' | null>(
    connectParams && def.kind === 'universal' ? 'new' : null)
  const [testMsg, setTestMsg] = useState<string | null>(null)

  async function toggle(i: Instance) {
    try {
      await update.mutateAsync({
        id: i.id,
        body: {
          label: i.label, enabled: !i.enabled, params: i.params,
          chat_id: i.chat_id, cooldown_min: i.cooldown_min,
        },
      })
    } catch { /* refetch покажет серверное состояние */ }
  }

  async function runTest(i: Instance) {
    setTestMsg(null)
    try {
      const r = await test.mutateAsync(i.id)
      setTestMsg(r.ok ? 'Тест отправлен ✓' : `Ошибка: ${r.error ?? 'не отправилось'}`)
    } catch {
      setTestMsg('Ошибка отправки')
    }
  }

  return (
    <div className="bg-ink border border-line p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/app/screeners/${def.id}`} className="font-semibold hover:text-fire transition-colors">
              {def.name}
            </Link>
            {isAdmin && (
              <span className="px-1.5 py-0.5 text-2xs font-mono bg-line/40 text-textDim">
                {def.kind === 'universal' ? 'universal' : def.signal_type}
              </span>
            )}
            {def.visibility === 'public' && !isAdmin && (
              <span className="px-1.5 py-0.5 text-2xs text-sky border border-sky/30">публичный</span>
            )}
            {def.visibility === 'assigned' && !isAdmin && (
              <span className="px-1.5 py-0.5 text-2xs text-fire border border-fire/30">назначен вам</span>
            )}
          </div>
          <p className="text-xs text-textDim mt-1 leading-snug max-w-2xl">{def.description}</p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {isAdmin && (
            <>
              <select
                value={def.visibility}
                onChange={(e) => updateDef.mutate({ id: def.id, body: { visibility: e.target.value as Visibility } })}
                className="bg-surface border border-line px-2 py-1 text-xs text-text outline-none"
                title="Кому доступен скринер"
              >
                {(['off', 'public', 'assigned'] as Visibility[]).map((v) => (
                  <option key={v} value={v}>{VIS_LABEL[v]}</option>
                ))}
              </select>
              <button
                onClick={() => setModal('access')}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-textDim border border-line hover:bg-line/20"
                title="Назначить юзерам"
              >
                <UsersIcon size={13} />
                {(def.assigned_user_ids ?? []).length || ''}
              </button>
              {def.kind === 'signal' && (
                <button
                  onClick={() => {
                    if (window.confirm(`Удалить скринер «${def.name}» и все его инстансы?`)) {
                      deleteDef.mutate(def.id)
                    }
                  }}
                  className="text-textFaint hover:text-rose px-1"
                  title="Удалить скринер"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </>
          )}
          <Link
            to={`/app/screeners/${def.id}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-textDim border border-line hover:bg-line/20"
            title="Страница скринера: бектест по параметрам"
          >
            <FlaskConical size={13} /> Бектест
          </Link>
          <button
            onClick={() => setModal('new')}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-pulse/15 text-pulse border border-pulse/30 hover:bg-pulse/25"
          >
            <Plus size={13} /> Подключить
          </button>
        </div>
      </div>

      {isAdmin && (def.instances_total ?? 0) > 0 && (
        <div className="text-2xs text-textFaint mt-2">
          Инстансов: {def.instances_enabled}/{def.instances_total} вкл.
        </div>
      )}

      {instances.length > 0 && (
        <div className="mt-3 border-t border-line/50 pt-1 divide-y divide-line/40">
          {instances.map((i) => (
            <div key={i.id} className="flex items-start gap-3 py-2.5">
              <button
                onClick={() => toggle(i)}
                className={cn(
                  'px-2 py-0.5 text-xs border shrink-0',
                  i.enabled
                    ? 'bg-pulse/15 text-pulse border-pulse/30'
                    : 'bg-line/30 text-textFaint border-line',
                )}
                title={i.enabled ? 'Выключить' : 'Включить'}
              >
                {i.enabled ? 'вкл' : 'выкл'}
              </button>
              <div className="min-w-0 flex-1 text-xs">
                <div className="text-textDim break-words">
                  {def.kind === 'universal' ? (
                    <>
                      <b className="text-text mr-1.5">{i.label || autoName(i.params)}</b>
                      {i.label && <span className="text-textFaint">{autoName(i.params)}</span>}
                      {filtersSummary(i.params) && (
                        <span className="block text-2xs text-sky/80">фильтры: {filtersSummary(i.params)}</span>
                      )}
                    </>
                  ) : (
                    <>
                      {i.label && <b className="text-text mr-1.5">{i.label}</b>}
                      {paramsSummary(i)}
                    </>
                  )}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 text-2xs text-textFaint tabular-nums">
                  <span title="Telegram-чат">чат <span className="font-mono">{i.chat_id || '—'}</span></span>
                  {i.cooldown_min > 0 && (
                    <span title="пауза между сигналами по одной монете">пауза {i.cooldown_min} мин</span>
                  )}
                  <span>отправлено {i.sent_count}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0 pt-0.5">
                <button
                  onClick={() => runTest(i)}
                  className="text-textFaint hover:text-sky"
                  title="Отправить тестовое сообщение"
                >
                  <Radio size={15} />
                </button>
                <button
                  onClick={() => setModal(i)}
                  className="text-textFaint hover:text-textDim"
                  title="Настройки"
                >
                  <Pencil size={15} />
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Отключить и удалить этот скринер?')) del.mutate(i.id)
                  }}
                  className="text-textFaint hover:text-rose"
                  title="Удалить"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {testMsg && <div className="text-2xs text-textDim mt-1.5">{testMsg}</div>}

      {modal === 'new' && (
        <InstanceModal def={def} inst={null}
          initParams={def.kind === 'universal' ? connectParams : undefined}
          initName={def.kind === 'universal' ? connectName : undefined}
          defaultChat={instances.find((x) => x.chat_id)?.chat_id}
          onClose={() => setModal(null)} />
      )}
      {modal !== null && modal !== 'new' && modal !== 'access' && (
        <InstanceModal def={def} inst={modal} onClose={() => setModal(null)} />
      )}
      {modal === 'access' && <AccessModal def={def} onClose={() => setModal(null)} />}
    </div>
  )
}

// ─── Лента сигналов ──────────────────────────────────────────────────

function fwdCell(v: number | null) {
  if (v == null) return <span className="text-textFaint">—</span>
  return (
    <span className={v >= 0 ? 'text-pulse' : 'text-rose'}>
      {v > 0 ? '+' : ''}{v.toFixed(2)}%
    </span>
  )
}

function metricsSummary(s: SignalRow): string {
  const m = s.metrics
  const parts: string[] = []
  if (m.price_pct != null) parts.push(`P ${Number(m.price_pct) > 0 ? '+' : ''}${m.price_pct}%/${m.price_window_min}м`)
  if (m.oi_pct != null) parts.push(`OI ${Number(m.oi_pct) > 0 ? '+' : ''}${m.oi_pct}%/${m.oi_window_min}м`)
  if (m.nd_pct != null) parts.push(`Δ ${Number(m.nd_pct) > 0 ? '+' : ''}${m.nd_pct}%/${m.nd_window_min}м`)
  if (m.gap_pct != null) parts.push(`Гап ${Number(m.gap_pct) > 0 ? '+' : ''}${m.gap_pct}%/${m.gap_window_min}м`)
  if (m.vol_x != null) parts.push(`Объём ×${m.vol_x}/${m.vol_window_min}м`)
  if (m.liq_usd != null) parts.push(`Ликв. $${Math.round(Number(m.liq_usd) / 1000)}k/${m.liq_window_min}м`)
  if (m.fund_pct != null) parts.push(`Фандинг ${m.fund_pct}%`)
  if (parts.length) return parts.join(' · ')
  const alt: string[] = []
  if (m.direction) alt.push(String(m.direction))
  if (m.dp_pct != null) alt.push(`dp ${m.dp_pct}%`)
  if (m.oi_change_pct != null) alt.push(`oi ${m.oi_change_pct}%`)
  return alt.join(' · ') || '—'
}

function SortHeader({ label, k, sort, order, onSort, className }: {
  label: string
  k: 'ts' | 'fwd15' | 'fwd1h' | 'fwd4h'
  sort: string
  order: 'asc' | 'desc'
  onSort: (k: 'ts' | 'fwd15' | 'fwd1h' | 'fwd4h') => void
  className?: string
}) {
  const active = sort === k
  return (
    <th className={cn('px-3 py-2 font-medium cursor-pointer select-none hover:text-text', className)}
        onClick={() => onSort(k)}>
      {label}{active ? (order === 'desc' ? ' ↓' : ' ↑') : ''}
    </th>
  )
}

function UserFilter({ userId, setUserId }: {
  userId: number; setUserId: (v: number) => void
}) {
  const { data } = useAdminUsers({ status: 'active', page: 1 })
  return (
    <select
      value={userId}
      onChange={(e) => setUserId(Number(e.target.value))}
      className="bg-surface border border-line px-2 py-1.5 text-sm text-text outline-none"
      title="Кому доставлялся сигнал"
    >
      <option value={0}>Все пользователи</option>
      {(data?.items ?? []).filter((u) => u.email).map((u) => (
        <option key={u.id} value={u.id}>{u.email}</option>
      ))}
    </select>
  )
}

function SignalsTab({ defs, isAdmin }: { defs: ScreenerDef[]; isAdmin: boolean }) {
  const [defId, setDefId] = useState(0)
  const [userId, setUserId] = useState(0)
  const [sort, setSort] = useState<'ts' | 'fwd15' | 'fwd1h' | 'fwd4h'>('ts')
  const [order, setOrder] = useState<'asc' | 'desc'>('desc')
  const [viewIdx, setViewIdx] = useState<number | null>(null)

  const { data, isLoading } = useScreenerSignals({
    screenerId: defId, limit: 100, userId, sort, order,
  })
  const signals = data?.signals ?? []

  function onSort(k: 'ts' | 'fwd15' | 'fwd1h' | 'fwd4h') {
    if (sort === k) setOrder(order === 'desc' ? 'asc' : 'desc')
    else { setSort(k); setOrder('desc') }
  }

  // сигналы -> формат EventViewer (те же поля)
  const events: BacktestEvent[] = useMemo(
    () => signals.map((s) => ({
      ts: s.ts, symbol: s.symbol, price: s.price, metrics: s.metrics,
    })),
    [signals])

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <select
          value={defId}
          onChange={(e) => setDefId(Number(e.target.value))}
          className="bg-surface border border-line px-2 py-1.5 text-sm text-text outline-none"
        >
          <option value={0}>Все скринеры</option>
          {defs.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
        {isAdmin && <UserFilter userId={userId} setUserId={setUserId} />}
        <span className="text-2xs text-textFaint">
          «Через 15 мин / 1 ч / 4 ч» — как изменилась цена после сигнала (рост — плюс).
          Нажмите на сигнал — откроется график.<span className="hidden md:inline"> Клик по заголовку — сортировка.</span>
        </span>
      </div>
      {isLoading && (
        <div className="px-5 py-8 text-center text-textFaint text-sm bg-ink border border-line">…</div>
      )}
      {!isLoading && signals.length === 0 && (
        <div className="px-5 py-8 text-center text-textFaint text-sm bg-ink border border-line">
          Срабатываний пока нет.
        </div>
      )}
      {signals.length > 0 && (
        <div className="md:hidden space-y-2">
          {signals.map((s, i) => (
            <button
              key={s.id} type="button" onClick={() => setViewIdx(i)}
              className="w-full text-left bg-ink border border-line p-3 text-xs hover:bg-line/20"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-text">{s.symbol}</span>
                <span className="text-textFaint">
                  {new Date(s.ts).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div className="text-textDim mt-0.5">
                {s.screener_key === 'universal' ? (s.label || autoName(s.params)) : s.screener_key}
              </div>
              <div className="text-textFaint mt-0.5">{metricsSummary(s)}</div>
              <div className="grid grid-cols-3 gap-2 mt-2 tabular-nums">
                {([['через 15 мин', s.fwd_15m_pct], ['через 1 ч', s.fwd_1h_pct], ['через 4 ч', s.fwd_4h_pct]] as const).map(([l, v]) => (
                  <div key={l}>
                    <div className="text-2xs text-textFaint">{l}</div>
                    <div>{fwdCell(v)}</div>
                  </div>
                ))}
              </div>
            </button>
          ))}
        </div>
      )}
      {signals.length > 0 && (
        <div className="hidden md:block bg-ink border border-line overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="text-left text-textFaint border-b border-line">
                <SortHeader label="Время" k="ts" sort={sort} order={order} onSort={onSort} />
                <th className="px-3 py-2 font-medium">Скринер</th>
                <th className="px-3 py-2 font-medium">Монета</th>
                <th className="px-3 py-2 font-medium">Метрики</th>
                <th className="px-3 py-2 font-medium text-right">Цена</th>
                <SortHeader label="через 15 мин" k="fwd15" sort={sort} order={order} onSort={onSort} className="text-right" />
                <SortHeader label="через 1 ч" k="fwd1h" sort={sort} order={order} onSort={onSort} className="text-right" />
                <SortHeader label="через 4 ч" k="fwd4h" sort={sort} order={order} onSort={onSort} className="text-right" />
              </tr>
            </thead>
            <tbody>
              {signals.map((s, i) => (
                <tr
                  key={s.id}
                  onClick={() => setViewIdx(i)}
                  className="border-b border-line/40 hover:bg-line/20 cursor-pointer"
                >
                  <td className="px-3 py-1.5 text-xs text-textDim">
                    {new Date(s.ts).toLocaleString('ru-RU', {
                      day: '2-digit', month: '2-digit',
                      hour: '2-digit', minute: '2-digit',
                    })}
                  </td>
                  <td className="px-3 py-1.5">
                    {s.screener_key === 'universal' ? (
                      <span className="text-xs text-text">{s.label || autoName(s.params)}</span>
                    ) : (
                      <span className="px-1.5 py-0.5 text-2xs font-mono bg-line/40 text-textDim">
                        {s.screener_key}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 font-medium">{s.symbol}</td>
                  <td className="px-3 py-1.5 text-xs text-textDim">{metricsSummary(s)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-textDim">
                    {s.price != null ? Number(s.price).toPrecision(6) : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fwdCell(s.fwd_15m_pct)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fwdCell(s.fwd_1h_pct)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fwdCell(s.fwd_4h_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewIdx !== null && events[viewIdx] && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-3 overflow-y-auto"
          onClick={() => setViewIdx(null)}
        >
          <div
            className="bg-ink border border-line w-full max-w-6xl mt-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-3 pt-2">
              <span className="text-xs text-textFaint">График события — Esc/клик мимо для закрытия</span>
              <button onClick={() => setViewIdx(null)} className="text-textFaint hover:text-textDim p-1">
                <X size={16} />
              </button>
            </div>
            <EventViewer events={events} index={viewIdx} setIndex={setViewIdx} />
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Страница ────────────────────────────────────────────────────────

export default function Screeners() {
  const { data, isLoading, isError } = useScreenersOverview()
  const [tab, setTab] = useState<'screeners' | 'signals'>('screeners')
  const [createOpen, setCreateOpen] = useState(false)
  const [search] = useSearchParams()
  const connectParams = useMemo(() => {
    try {
      const raw = search.get('connect')
      return raw ? (JSON.parse(raw) as UniversalParams) : undefined
    } catch { return undefined }
  }, [search])

  const isAdmin = data?.is_admin ?? false
  const available = data?.available ?? []
  const defs = useMemo(() => available.map((a) => a.def), [available])

  return (
    <div className="p-4 sm:p-6">
      <header className="mb-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Скринеры</h1>
          <p className="text-sm text-textDim mt-1">
            Подключите скринер, настройте условия и укажите Telegram-чат —
            бот будет присылать сигналы. Все срабатывания фиксируются.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <Link
            to="/app/screeners/setups"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-fire/40 text-fire hover:bg-fire/10 whitespace-nowrap"
          >
            <Trophy size={14} /> Подбор сетапов
          </Link>
          <div className="flex border border-line">
            <button
              onClick={() => setTab('screeners')}
              className={cn('px-3 py-1.5 text-sm', tab === 'screeners' ? 'bg-line/40 text-text' : 'text-textDim hover:text-text')}
            >
              Скринеры
            </button>
            <button
              onClick={() => setTab('signals')}
              className={cn('px-3 py-1.5 text-sm inline-flex items-center gap-1.5', tab === 'signals' ? 'bg-line/40 text-text' : 'text-textDim hover:text-text')}
            >
              <Activity size={14} /> Сигналы
            </button>
          </div>
          {isAdmin && tab === 'screeners' && (
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-pulse/15 text-pulse border border-pulse/30 hover:bg-pulse/25"
            >
              <Plus size={15} /> Signal-скринер
            </button>
          )}
        </div>
      </header>

      {isLoading && (
        <div className="px-5 py-10 text-center text-textFaint text-sm bg-ink border border-line">…</div>
      )}
      {isError && (
        <div className="px-5 py-10 text-center text-rose text-sm bg-ink border border-line">
          Не удалось загрузить скринеры.
        </div>
      )}

      {tab === 'screeners' && !isLoading && !isError && (
        <div className="space-y-3">
          {available.length === 0 && (
            <div className="px-5 py-10 text-center text-textFaint text-sm bg-ink border border-line">
              Вам пока не доступен ни один скринер. Обратитесь к администратору.
            </div>
          )}
          {available.map((a) => (
            <ScreenerCard key={a.def.id} item={a} isAdmin={isAdmin}
              connectParams={connectParams} connectName={search.get('name') ?? undefined} />
          ))}
        </div>
      )}

      {tab === 'signals' && <SignalsTab defs={defs} isAdmin={isAdmin} />}

      {createOpen && <CreateDefModal onClose={() => setCreateOpen(false)} />}
    </div>
  )
}
